import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Affix,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Select,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { ContactLevelEnum } from 'domains/orgDomain';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addressTranslator } from 'utils/address';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import { RULE_TEXT_LEN_255 } from 'utils/validators';

// Sentinel id for a linked user carried over from the contact that is not
// present in the accounts list (shown so the info is still visible).
const FALLBACK_USER_ID = -1;

const CtItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.org.saveContact
  );
  const item = useAppSelector((state) => state.org.curContact);
  const allAccounts = useAppSelector(
    (state) => state.account.allAccounts || []
  );

  const [contactLevel, setContactLevel] = useState<
    ContactLevelEnum | undefined
  >(item?.contactLevel);

  // Email carried over from the contact, used to match the linked user.
  const incomingEmail = item?.user?.email || item?.email;

  // The account in the list that matches the contact's linked user by email.
  const matchedAccount = useMemo(
    () =>
      incomingEmail
        ? allAccounts.find(
            (account) =>
              account.email?.toLowerCase() === incomingEmail.toLowerCase()
          )
        : undefined,
    [allAccounts, incomingEmail]
  );

  // When no account matches, fall back to the user info carried over from the
  // contact so the "Linked User" select still shows firstName/lastName/email.
  const fallbackUser =
    contactLevel === ContactLevelEnum.USER && incomingEmail && !matchedAccount
      ? {
          id: item?.user?.id ?? FALLBACK_USER_ID,
          firstName: item?.user?.firstName ?? item?.firstName ?? '',
          lastName: item?.user?.lastName ?? item?.lastName ?? '',
          email: incomingEmail,
        }
      : undefined;

  useEffect(() => {
    dispatch.account.getAllAccounts();
  }, [dispatch.account]);

  // For USER-level contacts, preselect the linked user (matched by email, or
  // the fallback user info carried over from the contact).
  useEffect(() => {
    if (item?.contactLevel !== ContactLevelEnum.USER || !incomingEmail) {
      return;
    }
    form.setFieldsValue({
      userId: matchedAccount
        ? matchedAccount.id
        : item?.user?.id ?? FALLBACK_USER_ID,
    });
  }, [matchedAccount, incomingEmail, item, form]);

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    const updated = Object.assign({}, item, values);
    await dispatch.org.saveContact(updated);
    onClose();
  };
  const onClose = () => {
    dispatch.org.setState({ curContact: undefined });
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    const updated = Object.assign({}, item, values);
    updated.id = undefined;
    await dispatch.org.saveContact(updated);
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.org.removeContact(item);
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.org.ct.form.title.edit')
                : t('platform.org.ct.form.title.create')}
            </Headline>
          }
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            ...(item?.id
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 160 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  return (
    <RevxSpin spinning={saving}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          className="platform-item-form"
          initialValues={{
            ...item,
            userId: item?.user?.id,
          }}
        >
          <Form.Item
            label={t('platform.org.ct.table.columns.firstName')}
            name={'firstName'}
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.table.columns.lastName')}
            name={'lastName'}
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.phoneNumber')}
            name="phoneNumber"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.table.columns.email')}
            name="email"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.table.columns.level')}
            name={'contactLevel'}
          >
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              onChange={(value: ContactLevelEnum) => {
                setContactLevel(value);
                if (value === ContactLevelEnum.ORG) {
                  form.setFieldsValue({ userId: undefined });
                }
              }}
            >
              <Select.Option value={ContactLevelEnum.ORG}>
                {ContactLevelEnum.ORG}
              </Select.Option>
              <Select.Option value={ContactLevelEnum.USER}>
                {ContactLevelEnum.USER}
              </Select.Option>
            </Select>
          </Form.Item>

          {contactLevel === ContactLevelEnum.USER && (
            <Form.Item
              label={t('platform.org.ct.table.columns.user')}
              name={'userId'}
            >
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {fallbackUser && (
                  <Select.Option
                    value={fallbackUser.id}
                    key={fallbackUser.id}
                  >
                    {`${`${fallbackUser.firstName} ${fallbackUser.lastName}`.trim()} (${
                      fallbackUser.email
                    })`}
                  </Select.Option>
                )}
                {allAccounts.map((account) => (
                  <Select.Option value={account.id} key={account.id}>
                    {`${`${account.firstName || ''} ${
                      account.lastName || ''
                    }`.trim()} (${account.email})`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label={t('platform.org.ct.table.columns.title')}
            name={'title'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.table.columns.department')}
            name={'department'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.table.columns.costCenter')}
            name={'costCenter'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.form.label.accountingLine')}
            name={'accountingLine'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.form.label.oracleBillingCode')}
            name={'oracleBillingCode'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.table.columns.company')}
            name={'company'}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.ct.form.label.defaultAddress')}
            name={'defaultAddressId'}
            hidden={!item?.id}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {item?.addresses?.map((addr) => (
                <Select.Option value={addr.id} key={addr.id}>
                  {addressTranslator.getCompactAddress(addr)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default CtItemForm;
